import React from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import Tooltip from 'components_new/atoms/Tooltip'

import ToggleButton from 'components_new/molecules/ToggleButton'
import { BOARD } from 'components_new/molecules/Node/accents'

import { useTestEnvironment } from 'redux/hooks/TestEnvironment'

interface EditToolbarProps {
  readOnly?: boolean
  editMode: boolean
  onToggleEdit: (nextEditMode: boolean) => void
  onOpenVersions: () => void
  onToggleChat: () => void
  onSave: () => void
  saveDisabled: boolean
  onTransfer: () => void

  // Why the promote is blocked right now (unsaved edits, an older version on
  // screen). Set ⇒ the button is disabled and the tooltip says why, because
  // the promote copies the saved version, not what the board shows. Board
  // state, so it comes from the parent — whether the action exists at all is
  // decided here.
  transferBlockedReason?: string
}

/**
 * Second row of the board top bar: grouped actions (add node, version
 * history) on the left, and the view/edit segmented switch plus the save
 * action on the right.
 */
const EditToolbar = (props: EditToolbarProps) => {
  const {
    readOnly,
    editMode,
    onToggleEdit,
    onOpenVersions,
    onToggleChat,
    onSave,
    saveDisabled,
    onTransfer,
    transferBlockedReason
  } = props

  // Only the test environment can promote a table, so the action is absent
  // rather than disabled when the board already shows production.
  const { variant } = useTestEnvironment()
  const canTransfer = variant === 'test'

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'common.white',
        borderBottom: `1px solid ${BOARD.borderSoft}`,
        display: 'flex',
        gap: 1,
        justifyContent: 'space-between',
        px: 3,
        py: 1
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        <Button
          onClick={onOpenVersions}
          startIcon={<Icon name="History" />}
          variant="text"
        >
          Versioner
        </Button>
        {!readOnly && (
          <Button
            onClick={onToggleChat}
            startIcon={<Icon name="AI" />}
            variant="text"
          >
            AI-assistent
          </Button>
        )}
      </Box>

      {!readOnly && (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          {canTransfer && (
            <Tooltip
              title={transferBlockedReason ?? ''}
              disableHoverListener={!transferBlockedReason}
            >
              <span>
                <Button
                  disabled={Boolean(transferBlockedReason)}
                  onClick={onTransfer}
                  startIcon={<Icon name="MoveUpOutlined" />}
                  variant="text"
                >
                  Överför till produktion
                </Button>
              </span>
            </Tooltip>
          )}
          <ToggleButton
            fullWidth={false}
            value={editMode}
            onChange={(_, next) => {
              if (next !== null && next !== editMode) {
                onToggleEdit(next)
              }
            }}
            items={[
              { iconName: 'VisibilityOutlined', title: 'Visa', value: false },
              { iconName: 'Edit', title: 'Redigera', value: true }
            ]}
            size="small"
            sx={{
              backgroundColor: BOARD.chipBackground,
              border: 'none',
              borderRadius: '10px',
              p: '3px',
              '& .MuiToggleButton-root': {
                borderRadius: '8px !important',
                color: 'text.secondary',
                px: 1.5,
                py: 0.5,
                textTransform: 'none'
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: 'common.white',
                boxShadow: BOARD.shadowRest,
                color: 'text.primary',
                '&:hover': { backgroundColor: 'common.white' }
              }
            }}
          />
          <Button
            disabled={!editMode || saveDisabled}
            onClick={onSave}
            startIcon={<Icon name="SaveOutlined" />}
            variant="text"
          >
            Spara
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default EditToolbar
